/**
 * Bloco centralizado para quando não há nenhuma linha a mostrar.
 *
 * Papel: mensagem e, opcionalmente, um botão de ação, dentro de uma borda sem
 * canto superior — pensada para encostar direto na tabela ou no cabeçalho de
 * seção que fica acima dela.
 *
 * Como usar: `mensagem` aceita um `ReactNode`, e não só texto, para quem
 * precisar de mais de uma frase — um segundo `<p>` dentro do fragmento
 * mantém cada frase no próprio nó de texto, o que a torna localizável sozinha
 * num teste, ao contrário de um `<br />` unindo as duas num nó só. `rotuloAcao`
 * e `onAcao` andam sempre juntos: informar só um dos dois não desenha botão
 * nenhum, porque um botão sem rótulo não tem o que anunciar e um rótulo sem
 * ação não tem o que fazer ao ser clicado.
 *
 * O botão vem sempre abaixo da mensagem, num bloco próprio a 20 px dela, mesmo
 * quando `mensagem` é uma frase solta sem `<p>`.
 */
import { type ComponentProps, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button';

interface VazioProps {
  /**
   * O que dizer quando não há nenhuma linha. Para mais de uma frase, use um
   * `<p>` por frase, nunca um `<br />` entre elas — o `<br />` uniria as duas
   * no mesmo nó de texto, e nenhuma das duas ficaria mais localizável sozinha
   * num teste que busque só uma delas.
   */
  mensagem: ReactNode;
  /** Rótulo do botão de ação; sem `onAcao` junto, nenhum botão é desenhado. */
  rotuloAcao?: string;
  /** Chamado ao clicar no botão de ação. */
  onAcao?: () => void;
  /** Desabilita o botão de ação, para quando ela já está em andamento. */
  acaoDesabilitada?: boolean;
  /**
   * Variante do botão de ação, repassada direto ao `Button`. Sem valor,
   * sai `'outline'` — o contorno cinza que a nova tentativa depois de um
   * erro usa; quem quiser o mesmo tom do botão que abre um cadastro passa
   * `'default'`.
   */
  varianteAcao?: ComponentProps<typeof Button>['variant'];
  className?: string;
}

/**
 * O bloco de "nada para mostrar".
 *
 * @param mensagem - O que dizer quando não há nenhuma linha; um `<p>` por
 *   frase, nunca `<br />` entre elas.
 * @param rotuloAcao - Rótulo do botão de ação; sem `onAcao` junto, nenhum
 *   botão é desenhado.
 * @param onAcao - Chamado ao clicar no botão de ação.
 * @param acaoDesabilitada - Desabilita o botão de ação.
 * @param varianteAcao - Variante do botão de ação; sem valor, sai `'outline'`.
 * @param className - Classes de quem chama, aplicadas depois das internas
 *   pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O bloco centralizado, com o botão quando houver um.
 */
export function Vazio({
  mensagem,
  rotuloAcao,
  onAcao,
  acaoDesabilitada,
  varianteAcao = 'outline',
  className,
}: VazioProps) {
  return (
    <div
      className={cn(
        'border border-t-0 border-linha-forte px-5 py-[70px] text-center text-sm text-texto-fraco',
        className,
      )}
    >
      {mensagem}
      {rotuloAcao !== undefined && onAcao !== undefined && (
        // O bloco próprio é o que põe o botão na linha de baixo quando
        // `mensagem` é texto solto: a margem num `inline-flex` não quebra linha.
        <div className="mt-5">
          <Button variant={varianteAcao} onClick={onAcao} disabled={acaoDesabilitada}>
            {rotuloAcao}
          </Button>
        </div>
      )}
    </div>
  );
}
